import { roundedPoint } from "../domain/location";
import type { GeoPoint } from "../domain/location";
import { positionOverallLimitMs } from "./PlatformServices";
import type { PlatformServices } from "./PlatformServices";

/** Browser objects the web platform uses (injected for tests). */
export interface WebPlatformTargets {
  readonly window: Pick<Window, "addEventListener" | "removeEventListener">;
  readonly document: Pick<Document, "addEventListener" | "removeEventListener" | "visibilityState">;
  readonly navigator: Pick<Navigator, "onLine"> &
    Partial<Pick<Navigator, "setAppBadge" | "clearAppBadge" | "geolocation">>;
  /** Timer of the overall limit of a position request. */
  readonly setTimeout?: (callback: () => void, delayMs: number) => unknown;
}

/**
 * {@link PlatformServices} of the browser and the installed PWA: `navigator.onLine` with the
 * `online`/`offline` events, `visibilitychange` for the foreground and the Badging API for the
 * icon of the installed app.
 */
export class WebPlatformServices implements PlatformServices {
  /** @inheritdoc */
  readonly name = "web";

  /** @inheritdoc */
  readonly isNative = false;

  private readonly targets: WebPlatformTargets;

  /**
   * Creates the services.
   *
   * @param targets - Window, document and navigator.
   */
  constructor(targets: WebPlatformTargets) {
    this.targets = targets;
  }

  /** @inheritdoc */
  isOnline(): Promise<boolean> {
    const online = Promise.resolve(this.targets.navigator.onLine);

    return online;
  }

  /** @inheritdoc */
  onNetworkChange(listener: (online: boolean) => void): () => void {
    const onOnline = (): void => {
      listener(true);
    };
    const onOffline = (): void => {
      listener(false);
    };
    this.targets.window.addEventListener("online", onOnline);
    this.targets.window.addEventListener("offline", onOffline);
    const unsubscribe = (): void => {
      this.targets.window.removeEventListener("online", onOnline);
      this.targets.window.removeEventListener("offline", onOffline);
    };

    return unsubscribe;
  }

  /** @inheritdoc */
  onResume(listener: () => void): () => void {
    const onVisibilityChange = (): void => {
      if (this.isInForeground()) {
        listener();
      }
    };
    this.targets.document.addEventListener("visibilitychange", onVisibilityChange);
    const unsubscribe = (): void => {
      this.targets.document.removeEventListener("visibilitychange", onVisibilityChange);
    };

    return unsubscribe;
  }

  /** @inheritdoc */
  isInForeground(): boolean {
    const visible = this.targets.document.visibilityState === "visible";

    return visible;
  }

  /** @inheritdoc */
  onAppUrlOpen(): () => void {
    // Browsers open URLs as page loads; there is no app URL event.
    const unsubscribe = (): void => undefined;

    return unsubscribe;
  }

  /** @inheritdoc */
  onNotificationOpened(): () => void {
    // The service worker focuses or opens the page of a notification itself (T-071).
    const unsubscribe = (): void => undefined;

    return unsubscribe;
  }

  /** @inheritdoc */
  async setBadge(count: number): Promise<void> {
    const { setAppBadge, clearAppBadge } = this.targets.navigator;

    try {
      // Only installed apps of supporting browsers show badges; elsewhere the calls fail or are missing.
      if (count > 0 && setAppBadge !== undefined) {
        await setAppBadge.call(this.targets.navigator, count);
      } else if (count <= 0 && clearAppBadge !== undefined) {
        await clearAppBadge.call(this.targets.navigator);
      }
    } catch {
      // The badge is a convenience; the open days are shown in the application.
    }
  }

  /** @inheritdoc */
  clearDeliveredReminders(): Promise<void> {
    // Web push notifications replace each other by tag and close on a tap (T-071); none are removed.
    const done = Promise.resolve();

    return done;
  }

  /** @inheritdoc */
  currentPosition(timeoutMs: number): Promise<GeoPoint | null> {
    const geolocation = this.targets.navigator.geolocation;
    const schedule =
      this.targets.setTimeout ?? ((callback, delayMs): unknown => setTimeout(callback, delayMs));

    if (geolocation === undefined) {
      const unavailable = Promise.resolve(null);
      return unavailable;
    }

    const position = new Promise<GeoPoint | null>((resolve) => {
      schedule(() => {
        resolve(null);
      }, positionOverallLimitMs);

      try {
        // Coarse position, a fresh one, and no watch: one request per clock event (spec 12.4).
        geolocation.getCurrentPosition(
          (result) => {
            resolve(roundedPoint(result.coords.latitude, result.coords.longitude));
          },
          () => {
            resolve(null);
          },
          { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 0 },
        );
      } catch {
        resolve(null);
      }
    });

    return position;
  }
}
